import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { slidesFor } from '../lib/whatsNew.js';

// "What's new" — the release showcase. Same build as the TherapieMaps popup:
// one slide per feature with a small animated illustration that *demonstrates*
// it, a tag → title → description that drop in, dots, Back / Next, and a
// "Don't show again" checkbox. Which slides appear depends on the signed-in
// user's capabilities (lib/whatsNew.js); every slide leads to its Knowledge
// article. The art is plain markup + CSS animation (global.css .wn-*); the
// key on the hero remounts it per slide so the motion replays. Reduced-motion
// users get the finished state.

const Bell = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /><line x1="3" y1="3" x2="21" y2="21" />
  </svg>
);
const BellPlain = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);
const Tick = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
);
const DashCircle = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0M4.5 7.5a.5.5 0 0 0 0 1h7a.5.5 0 0 0 0-1z" /></svg>
);
const EyeSlash = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" /><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

/* Clinic: a Supervisor message, the clock chip, Acknowledge → paused. */
const ArtSlaClinic = () => (
  <div className="wn-mini wn-mini--sla">
    <div className="wn-chip wn-chip--swap">
      <span className="wn-chip-a">Clinic response · 5h 40m left</span>
      <span className="wn-chip-b"><Bell /> Waiting on Supervisor</span>
    </div>
    <div className="wn-msg">
      <span className="wn-avatar">SV</span>
      <div className="wn-msg-body">
        <div className="wn-msg-meta">Supervisor · 10:42</div>
        <div className="wn-msg-text">Please add the consent form and the laser settings.</div>
        <button type="button" className="wn-ack" tabIndex={-1}>
          <span className="wn-ack-a">Acknowledge</span>
          <span className="wn-ack-b"><Tick /> Acknowledged</span>
        </button>
      </div>
    </div>
  </div>
);

/* Supervisor: the clocks row with a held chip, Manage and a snooze pill. */
const ArtSlaSupervisor = () => (
  <div className="wn-mini wn-mini--sup">
    <div className="wn-row">
      {/* the merged pill: clock text · divider · bell + chevron (the Manage trigger) */}
      <div className="wn-chip wn-chip--warn wn-chip--split">
        <span className="wn-chip-text"><Bell /> Waiting on Supervisor</span>
        <span className="wn-chip-manage"><BellPlain /> <span className="wn-chev">▾</span></span>
      </div>
    </div>
    <div className="wn-menu">
      <div className="wn-menu-item wn-menu-item--1">Resume the clinic clock</div>
      <div className="wn-menu-item wn-menu-item--2">Further action required</div>
      <div className="wn-menu-item wn-menu-item--3">Suppress reminders</div>
    </div>
    <div className="wn-snooze"><span className="wn-moon">☾</span> Snooze SLA notifications · 3 days</div>
  </div>
);

const UploadIcon = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5" /><path d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708z" /></svg>
);
const PencilSquare = () => (
  <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" /><path d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z" /></svg>
);

/* Waiver: the real slot card — state icon, title + status, stacked actions on
   the right (text then icon). Record no consent form is pressed, the card turns
   amber, the status line explains, and the button becomes Withdraw. */
const ArtWaiver = () => (
  <div className="wn-mini wn-mini--waiver">
    <div className="wn-slot">
      <span className="wn-slot-icon"><DashCircle /></span>
      <div className="wn-slot-text">
        <div className="wn-slot-title">Consent form</div>
        <div className="wn-slot-sub">
          <span className="wn-slot-a">Required</span>
          <span className="wn-slot-b">No consent form · client declined to sign</span>
        </div>
      </div>
      <div className="wn-slot-actions">
        <span className="wn-sbtn">Upload <UploadIcon /></span>
        <span className="wn-sbtn wn-sbtn--press">
          <span className="wn-sbtn-a">Record no consent form <PencilSquare /></span>
          <span className="wn-sbtn-b">Withdraw <PencilSquare /></span>
        </span>
      </div>
    </div>
  </div>
);

/* Stale cases: two new columns and an urgent row. */
const ArtStale = () => (
  <div className="wn-mini wn-mini--table">
    <div className="wn-th"><span>Case</span><span>Status</span><span className="wn-new">Last activity</span><span className="wn-new">SLA</span></div>
    {[
      ['AE-2041', 'Pending', '58d ago', 'Clinic · 2d over', 'danger'],
      ['AE-2036', 'Pending', '31d ago', 'Clinic · 5h 10m', 'info'],
      ['AE-2028', 'Open', '3h ago', '—', ''],
    ].map(([id, st, ago, sla, tone], i) => (
      <div className={`wn-tr wn-tr--${i + 1}`} key={id}>
        <span className="wn-mono">{id}</span><span>{st}</span>
        <span className={Number.parseInt(ago, 10) >= 30 ? 'wn-stale' : ''}>{ago}</span>
        <span>{tone ? <span className={`wn-pill wn-pill--${tone}`}>{sla}</span> : '—'}</span>
      </div>
    ))}
    <div className="wn-inactive">Inactive for · 30+ days</div>
  </div>
);

/* Column picker: the table button opens the tick list and a column joins the table. */
const ArtColumns = () => (
  <div className="wn-mini wn-mini--cols">
    <div className="wn-cols-bar">
      <span className="wn-cols-chip">Selected · 284</span><span className="wn-cols-chip">All · 1302</span>
      <span className="wn-cols-btn"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21.187 5.998c-.202-.889-.533-1.564-1.078-2.109C18.718 2.498 16.479 2.498 12 2.498s-6.718 0-8.109 1.391S2.5 7.52 2.5 11.998s0 6.718 1.391 8.109S7.52 21.498 12 21.498s6.718 0 8.109-1.391c.545-.545.876-1.22 1.078-2.109" /><path d="M21.5 12h-4m0 0h-4m4 0v4m0-4V8" /><path d="M9 21.5v-19" /><path d="M2.5 15.5H9M2.5 8.5H9" /></svg></span>
    </div>
    <div className="wn-cols-menu">
      {[['Clinic', true], ['Severity', true], ['Last activity', true], ['SLA', false]].map(([l, on], i) => (
        <div className={`wn-cols-row${i === 3 ? ' wn-cols-row--tick' : ''}`} key={l}><span className={`wn-cb${on ? ' on' : ''}`} /> {l}</div>
      ))}
    </div>
    <div className="wn-cols-table"><span>Case</span><span>Severity</span><span>Last activity</span><span className="wn-cols-new">SLA</span></div>
  </div>
);

/* Pickers: the Tags group fills with has / doesn't-have pills. */
const ArtPickers = () => (
  <div className="wn-mini wn-mini--pickers">
    <div className="wn-group-title">Tags <span className="wn-plus">+</span></div>
    <div className="wn-pills">
      <span className="wn-tagpill wn-tagpill--add wn-tagpill--1">+ Legal</span>
      <span className="wn-tagpill wn-tagpill--remove wn-tagpill--2">− SP migrated</span>
      <span className="wn-tagpill wn-tagpill--add wn-tagpill--3">+ No consent</span>
    </div>
    <div className="wn-tri-row">
      <span>Legal</span>
      <span className="wn-tri"><i>−</i><i className="wn-tri-dot"><b /></i><i className="wn-tri-on">+</i></span>
    </div>
  </div>
);

/* Approvals: a request row, Approve turns into Approved. */
const ArtApprovals = () => (
  <div className="wn-mini wn-mini--inbox">
    <div className="wn-inbox-row">
      <span className="wn-avatar">CK</span>
      <div className="wn-msg-body">
        <div className="wn-msg-text">Access request · Therapie Clinic Naas</div>
        <div className="wn-msg-meta">Front of house · 5 min ago</div>
      </div>
      <button type="button" className="wn-approve" tabIndex={-1}><span className="wn-ack-a">Approve</span><span className="wn-ack-b"><Tick /> Approved</span></button>
    </div>
  </div>
);

/* Shared device sign-in: pick a name, PIN dots fill. */
const ArtSignin = () => (
  <div className="wn-mini wn-mini--signin">
    <div className="wn-signin-name"><span className="wn-avatar">MK</span> Working as <strong>M. K.</strong></div>
    <div className="wn-pin">{[1, 2, 3, 4, 5, 6].map(n => <span key={n} className={`wn-pin-dot wn-pin-dot--${n}`} />)}</div>
  </div>
);

/* Privacy: a photo thumbnail frosts over. */
const ArtPrivacy = () => (
  <div className="wn-mini wn-mini--privacy">
    <div className="wn-thumb">
      <div className="wn-thumb-img" />
      <div className="wn-thumb-frost"><EyeSlash /></div>
    </div>
    <div className="wn-thumb wn-thumb--doc"><span className="wn-doc">PDF</span></div>
    <div className="wn-priv-switch"><span className="wn-priv-knob" /></div>
  </div>
);

/* Case page: header with badges as controls and the clocks row. */
const ArtLayout = () => (
  <div className="wn-mini wn-mini--layout">
    <div className="wn-hdr-id">AE-2048</div>
    <div className="wn-hdr-title">Therapie Clinic Naas <span>Leinster</span></div>
    <div className="wn-hdr-badges">
      <span className="wn-badge wn-badge--sev">● Code 2 ▾</span>
      <span className="wn-badge wn-badge--status">● Pending ▾</span>
      <span className="wn-badge wn-badge--tag">AWI</span>
    </div>
    <div className="wn-clocks">
      <span className="wn-pill wn-pill--info">Clinic response · 5h 40m</span>
      <span className="wn-timeline"><i /><i /><i /></span>
    </div>
  </div>
);

/* Saved views: the title with a star + a dropdown of the user's views. */
const ArtSavedViews = () => (
  <div className="wn-mini wn-mini--views">
    <div className="wn-views-title">
      <span>All Cases</span>
      <span className="wn-views-star" aria-hidden="true">★</span>
      <span className="wn-views-chev" aria-hidden="true">▾</span>
    </div>
    <div className="wn-views-menu">
      <div className="wn-views-item">All Cases</div>
      <div className="wn-views-item is-on"><span className="wn-views-tick" aria-hidden="true">✓</span> My open Code 4s</div>
      <div className="wn-views-item"><span className="wn-views-def">Default</span> Belfast this week</div>
    </div>
  </div>
);

const ART = {
  'sla-clinic': ArtSlaClinic,
  'saved-views': ArtSavedViews,
  'sla-supervisor': ArtSlaSupervisor,
  waiver: ArtWaiver,
  'stale-cases': ArtStale,
  columns: ArtColumns,
  pickers: ArtPickers,
  approvals: ArtApprovals,
  'clinic-signin': ArtSignin,
  privacy: ArtPrivacy,
  'case-layout': ArtLayout,
};

export default function WhatsNewModal({ open, user, onClose, onOpenArticle }) {
  const slides = slidesFor(user);
  const [i, setI] = useState(0);
  const [dontShow, setDontShow] = useState(false);

  useEffect(() => { if (open) { setI(0); setDontShow(false); } }, [open]);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose(dontShow);
      else if (e.key === 'ArrowRight') setI(n => Math.min(n + 1, slides.length - 1));
      else if (e.key === 'ArrowLeft') setI(n => Math.max(n - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, dontShow, onClose, slides.length]);

  if (!open || slides.length === 0) return null;
  const idx = Math.min(i, slides.length - 1);
  const s = slides[idx];
  const Art = ART[s.key];
  const last = idx === slides.length - 1;
  const close = () => onClose(dontShow);

  return createPortal(
    <div className="wn-overlay" onMouseDown={close}>
      <div className="wn-card" role="dialog" aria-modal="true" aria-label="What’s new" onMouseDown={e => e.stopPropagation()}>
        <button type="button" className="wn-x" onClick={close} aria-label="Close">×</button>
        <div className={`wn-hero wn-hero--${s.key}`}>
          <div className="wn-hero-art" key={`art-${s.key}`}>{Art ? <Art /> : null}</div>
        </div>
        <div className="wn-body">
          <div className="wn-drop-text" key={`drop-${s.key}`}>
            <span className="wn-tag">{s.tag}</span>
            <h2 className="wn-title">{s.title}</h2>
            <p className="wn-desc">{s.desc}</p>
            {user?.canSeeKnowledge !== false && (
              <button type="button" className="wn-more" onClick={() => onOpenArticle(s.article)}>
                Read more in Knowledge <span aria-hidden="true">→</span>
              </button>
            )}
          </div>
        </div>
        <div className="wn-dots" role="tablist" aria-label="Features">
          {slides.map((sl, n) => (
            <button key={sl.key} type="button" className={`wn-dot-nav${n === idx ? ' is-on' : ''}`}
              role="tab" aria-selected={n === idx} aria-label={sl.title} onClick={() => setI(n)} />
          ))}
        </div>
        <div className="wn-foot">
          <label className="wn-check">
            <input type="checkbox" checked={dontShow} onChange={e => setDontShow(e.target.checked)} />
            Don’t show again
          </label>
          <div className="wn-nav">
            {idx > 0 && <button type="button" className="btn sm ghost" onClick={() => setI(n => n - 1)}>Back</button>}
            {last
              ? <button type="button" className="btn sm primary" onClick={close}>Got it</button>
              : <button type="button" className="btn sm primary" onClick={() => setI(n => n + 1)}>Next</button>}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
